import { type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { track } from '@vercel/analytics';
import {
  AnimalABCsGlyph,
  PekeGlyph,
  PepeGlyph,
  SuperpowersGlyph,
} from './wlGlyphsHome';

type PrimaryGameKey = 'animal-abcs' | 'tree-level' | 'creature-superpowers' | 'peke-level';

export interface WonderLittleHomeGridStrings {
  animalABCsTitle: string;
  animalABCsDesc: string;
  animalABCsButton: string;
  treeTitle: string;
  treeDesc: string;
  treeButton: string;
  creatureSuperpowersTitle: string;
  creatureSuperpowersDesc: string;
  creatureSuperpowersButton: string;
  pekeTitle: string;
  pekeDesc: string;
  pekeButton: string;
  crossAppBannerTitle: string;
  middleGradesTitle: string;
  middleGradesDesc: string;
  middleGradesButton: string;
  ecoDesignLabTitle: string;
  ecoDesignLabDesc: string;
  adultsTitle: string;
  adultsDesc: string;
  adultsButton: string;
}

interface WonderLittleHomeGridProps {
  strings: WonderLittleHomeGridStrings;
  language: 'en' | 'es';
  onSelectGame: (game: PrimaryGameKey, color: string) => void;
}

const TILE = {
  bgAlt: '#fbfbe8',
  white: '#ffffff',
  inkSoft: '#355c46',
  g200: '#c6e6d4',
  g400: '#5fbd8b',
  g500: '#48a58a',
  g700: '#358a73',
  g800: '#2a6d5b',
} as const;

const Serif: CSSProperties = { fontFamily: "'Fraunces', Georgia, serif" };
const Sans: CSSProperties = { fontFamily: "'Inter', system-ui, -apple-system, sans-serif" };

type GlyphProps = { size?: number; fg: string; bg: string };

type PrimaryConfig = {
  key: PrimaryGameKey;
  bg: string;
  fg: string;
  ink: string;
  arrow: string;
  Glyph: (p: GlyphProps) => JSX.Element;
  title: string;
  cta: string;
  analyticsName: string;
  /** When true, the glyph sits on the left and the text block is right-aligned. */
  imageOnLeft?: boolean;
};

type SecondaryConfig = {
  key: string;
  bg: string;
  fg: string;
  title: string;
  href: string;
  analyticsName: string;
};

function PrimaryCard({
  config,
  language,
  onSelectGame,
}: {
  config: PrimaryConfig;
  language: 'en' | 'es';
  onSelectGame: (game: PrimaryGameKey, color: string) => void;
}) {
  const { key, bg, fg, ink, arrow, Glyph, title, cta, analyticsName, imageOnLeft } = config;
  return (
    <motion.button
      type="button"
      whileHover={{ y: -2 }}
      whileTap={{ y: 0, filter: 'brightness(0.96)' }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
      onClick={() => {
        track('Activity Selected', {
          activity: key,
          activityName: analyticsName,
          language,
        });
        onSelectGame(key, bg);
      }}
      style={{
        width: '100%',
        textAlign: 'left',
        background: bg,
        color: ink,
        border: 'none',
        borderRadius: 24,
        padding: '28px 32px 28px',
        cursor: 'pointer',
        minHeight: 280,
        display: 'flex',
        flexDirection: imageOnLeft ? 'row-reverse' : 'row',
        alignItems: 'stretch',
        gap: 20,
        fontFamily: 'inherit',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          textAlign: imageOnLeft ? 'right' : 'left',
        }}
      >
        <h3
          style={{
            ...Sans,
            margin: '0 0 18px',
            fontSize: 31,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            lineHeight: 1.1,
          }}
        >
          {title}
        </h3>
        <span
          style={{
            ...Sans,
            fontSize: 22,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: imageOnLeft ? 'flex-end' : 'flex-start',
            gap: 6,
            color: arrow,
          }}
        >
          {cta} <span aria-hidden="true">→</span>
        </span>
      </div>
      <div style={{ flexShrink: 0, alignSelf: 'flex-end', lineHeight: 0 }}>
        <Glyph size={180} fg={fg} bg={bg} />
      </div>
    </motion.button>
  );
}

function SecondaryCard({ config, language }: { config: SecondaryConfig; language: 'en' | 'es' }) {
  const { bg, fg, title, href, analyticsName, key } = config;
  const explore = language === 'es' ? 'Explorar' : 'Explore';
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ y: -2 }}
      whileTap={{ y: 0, filter: 'brightness(0.96)' }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
      onClick={() => {
        track('External Link Clicked', {
          destination: analyticsName,
          url: href,
          language,
          source: key,
        });
      }}
      style={{
        width: '100%',
        textAlign: 'left',
        background: bg,
        color: fg,
        border: 'none',
        borderRadius: 18,
        padding: '20px 22px',
        cursor: 'pointer',
        minHeight: 88,
        display: 'flex',
        alignItems: 'center',
        fontFamily: 'inherit',
        textDecoration: 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          ...Sans,
          fontSize: 20,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {`${explore} ${title}`} <span aria-hidden="true">→</span>
      </span>
    </motion.a>
  );
}

export function WonderLittleHomeGrid({
  strings,
  language,
  onSelectGame,
}: WonderLittleHomeGridProps) {
  const primaries: PrimaryConfig[] = [
    {
      key: 'animal-abcs',
      bg: TILE.g200,
      fg: TILE.g700,
      ink: TILE.g800,
      arrow: TILE.g700,
      Glyph: AnimalABCsGlyph,
      title: strings.animalABCsTitle,
      cta: strings.animalABCsButton,
      analyticsName: 'Animal ABCs, 123s',
    },
    {
      key: 'tree-level',
      bg: TILE.g400,
      fg: TILE.white,
      ink: TILE.white,
      arrow: TILE.white,
      Glyph: PepeGlyph,
      title: strings.treeTitle,
      cta: strings.treeButton,
      analyticsName: 'Pepe Knows',
      imageOnLeft: true,
    },
    {
      key: 'creature-superpowers',
      bg: TILE.g500,
      fg: TILE.white,
      ink: TILE.white,
      arrow: TILE.white,
      Glyph: SuperpowersGlyph,
      title: strings.creatureSuperpowersTitle,
      cta: strings.creatureSuperpowersButton,
      analyticsName: 'Creature Superpowers',
    },
    {
      key: 'peke-level',
      bg: TILE.g700,
      fg: TILE.white,
      ink: TILE.white,
      arrow: TILE.white,
      Glyph: PekeGlyph,
      title: strings.pekeTitle,
      cta: strings.pekeButton,
      analyticsName: "Peke's Adventures",
      imageOnLeft: true,
    },
  ];

  const secondaries: SecondaryConfig[] = [
    {
      key: 'middle-grades',
      bg: TILE.g400,
      fg: TILE.white,
      title: strings.middleGradesTitle,
      href: 'https://eco-games-beta.vercel.app/',
      analyticsName: 'middle-school-games',
    },
    {
      key: 'eco-design-lab',
      bg: TILE.g500,
      fg: TILE.white,
      title: strings.ecoDesignLabTitle,
      href: 'https://eco-design-lab.vercel.app/',
      analyticsName: 'eco-design-lab',
    },
    {
      key: 'adult-games',
      bg: TILE.g700,
      fg: TILE.white,
      title: strings.adultsTitle,
      href: 'https://creative-eco-games.vercel.app/',
      analyticsName: 'adult-games',
    },
  ];

  return (
    <div
      className="wl-home-root"
      style={{ width: '100%', padding: '8px 16px 24px' }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 20,
        }}
        className="wl-primary-grid"
      >
        {primaries.map((p) => (
          <PrimaryCard key={p.key} config={p} language={language} onSelectGame={onSelectGame} />
        ))}
      </div>

      <section
        style={{
          background: TILE.bgAlt,
          borderRadius: 22,
          padding: '32px 32px',
          margin: '32px 0 0',
        }}
      >
        <h2
          style={{
            ...Serif,
            fontSize: 22,
            fontWeight: 700,
            color: TILE.g800,
            textAlign: 'left',
            margin: '0 0 16px',
          }}
        >
          {strings.crossAppBannerTitle}
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 14,
          }}
          className="wl-secondary-grid"
        >
          {secondaries.map((s) => (
            <SecondaryCard key={s.key} config={s} language={language} />
          ))}
        </div>
      </section>

      <style>{`
        @media (min-width: 720px) {
          .wl-home-root { padding: 16px 36px 32px !important; }
          .wl-primary-grid { grid-template-columns: 1fr 1fr !important; gap: 28px !important; }
          .wl-secondary-grid { grid-template-columns: 1fr 1fr 1fr !important; gap: 20px !important; }
        }
      `}</style>

      <p style={{ ...Sans, color: TILE.inkSoft, fontSize: 13, textAlign: 'center', margin: '24px 0 0' }}>
        MVP Demo ✨
      </p>
    </div>
  );
}
